import { Button, Input, Space, Table } from 'antd'
import iconEmpty from 'icons/createproject/supportneed.svg'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { RedoOutlined, SearchOutlined } from '@ant-design/icons'
import { getBusinessUnitList } from 'api/MasterData/business'
import { getCookie } from 'helper/cookie'
import lang from 'lang/en'

const BusinessUnitContainer = styled.div``
const EmptyContainer = styled.div``
const Container = styled.div``
const Header = styled.div``
const Body = styled.div``

const BusinessUnit = () => {
  const token = getCookie('access_token_admin')
  const language = getCookie('lang')
  const word = lang[language]

  const [dataBu, setDataBu] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState({
    pageSize: 10,
    total: 0,
    current: 1,
    showQuickJumper: true,
    showTotal: (total, range) => (
      <p className="small-show-total">{`${range[0]}-${range[1]} of ${total}`}</p>
    ),
  })

  // search params state
  const [searchParams, setSearchParams] = useState({
    code: '',
    name_th: '',
    name_en: '',
    status: '',
  })

  // sort state — order_by ตรงกับ enum ของ API, order: 'asc' | 'desc' | ''
  const [sortInfo, setSortInfo] = useState({ order_by: '', order: '' })

  // ref สำหรับ input ใน filterDropdown
  const searchInputRef = useRef(null)

  // ref เก็บค่า searchParams ล่าสุดเสมอ เพื่อแก้ stale closure ใน filterDropdown callbacks
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  // ref เก็บค่า sortInfo ล่าสุดเสมอ เพื่อแก้ stale closure ใน pagination callbacks
  const sortInfoRef = useRef(sortInfo)
  sortInfoRef.current = sortInfo

  const getDataBusinessUnit = async ({ page, per_page, params, sort } = {}) => {
    setIsLoading(true)
    const currentParams = params ?? searchParams
    const currentSort = sort ?? sortInfoRef.current
    await getBusinessUnitList({
      token,
      page: page ?? pagination.current,
      per_page: per_page ?? pagination.pageSize,
      code: currentParams.code || undefined,
      name_th: currentParams.name_th || undefined,
      name_en: currentParams.name_en || undefined,
      status: currentParams.status || undefined,
      order_by: currentSort.order_by || undefined,
      order: currentSort.order || undefined,
    })
      .then((response) => {
        const { status, data, error } = response
        if (error || status !== 200) {
          setDataBu([])
          setIsLoading(false)
          return
        }
        setDataBu(data.result?.data ?? [])
        setPagination((prev) => ({
          ...prev,
          current: data.result?.current_page ?? page ?? prev.current,
          pageSize: per_page ?? prev.pageSize,
          total: data.result?.total ?? 0,
        }))
        setIsLoading(false)
      })
      .catch((error) => {
        setDataBu([])
        setIsLoading(false)
        console.error(error)
      })
  }

  useEffect(() => {
    if (token) {
      getDataBusinessUnit({ page: 1, per_page: pagination.pageSize, params: searchParams })
    }
  }, [token])

  const toFilteredValue = (value) => (value ? [value] : null)

  // map dataIndex → order_by value ที่ API รับ
  const columnOrderByMap = {
    business_code: 'code',
    local_name: 'name',
    english_name: 'name_en',
    status: 'status',
  }

  // แปลง sortInfo เป็น sortOrder ของ Ant Design ('ascend' | 'descend' | null)
  const toSortOrder = (orderByValue) => {
    if (sortInfo.order_by !== orderByValue || !sortInfo.order) return null
    return sortInfo.order === 'asc' ? 'ascend' : 'descend'
  }

  // server-side search per column
  const handleConfirmSearch = (selectedKeys, confirm, paramKey) => {
    confirm()
    const newParams = { ...searchParamsRef.current, [paramKey]: selectedKeys[0] ?? '' }
    setSearchParams(newParams)
    getDataBusinessUnit({ page: 1, per_page: pagination.pageSize, params: newParams })
  }

  const getServerSearchProps = (dataIndex, paramKey) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleConfirmSearch(selectedKeys, confirm, paramKey)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            className="mini-search-btn-admin"
            onClick={() => handleConfirmSearch(selectedKeys, confirm, paramKey)}
          >
            <SearchOutlined className="icon" />
            Search
          </Button>
          <Button
            type="primary"
            className="mini-search-btn-admin-reset"
            onClick={() => {
              clearFilters()
              const newParams = { ...searchParamsRef.current, [paramKey]: '' }
              setSearchParams(newParams)
              getDataBusinessUnit({ page: 1, per_page: pagination.pageSize, params: newParams })
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  })

  const columns = [
    {
      title: <h5>Business Unit Code</h5>,
      dataIndex: 'business_code',
      sorter: true,
      sortOrder: toSortOrder(columnOrderByMap.business_code),
      ellipsis: true,
      filteredValue: toFilteredValue(searchParams.code),
      ...getServerSearchProps('business_code', 'code'),
    },
    {
      title: <h5>Local Business Unit Name</h5>,
      dataIndex: 'local_name',
      sorter: true,
      sortOrder: toSortOrder(columnOrderByMap.local_name),
      ellipsis: true,
      filteredValue: toFilteredValue(searchParams.name_th),
      ...getServerSearchProps('local_name', 'name_th'),
    },
    {
      title: <h5>English Business Unit Name</h5>,
      dataIndex: 'english_name',
      sorter: true,
      sortOrder: toSortOrder(columnOrderByMap.english_name),
      ellipsis: true,
      filteredValue: toFilteredValue(searchParams.name_en),
      ...getServerSearchProps('english_name', 'name_en'),
    },
    {
      title: <h5>Status</h5>,
      dataIndex: 'status',
      width: 180,
      render: (text) => <span>{text}</span>,
      sorter: true,
      sortOrder: toSortOrder(columnOrderByMap.status),
      ellipsis: true,
      filteredValue: toFilteredValue(searchParams.status),
      ...getServerSearchProps('status', 'status'),
    },
  ]

  const dataSource = dataBu.map((item) => ({
    key: item.id,
    id: item.id,
    business_code: item.code,
    local_name: item.name,
    english_name: item.name_en,
    status: item.status,
  }))

  const onTableChange = (paginationInfo, filters, sorter, extra) => {
    // filter changes are handled by handleConfirmSearch — skip to avoid duplicate fetch
    if (extra.action === 'filter') return

    if (extra.action === 'sort') {
      const newSort = {
        order_by: sorter.order ? columnOrderByMap[sorter.field] ?? '' : '',
        order: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : '',
      }
      setSortInfo(newSort)
      getDataBusinessUnit({ page: 1, per_page: pagination.pageSize, params: searchParamsRef.current, sort: newSort })
      return
    }

    // paginate
    const newPage = paginationInfo.current
    const newPerPage = paginationInfo.pageSize
    setPagination((prev) => ({ ...prev, current: newPage, pageSize: newPerPage }))
    getDataBusinessUnit({ page: newPage, per_page: newPerPage, params: searchParamsRef.current, sort: sortInfoRef.current })
  }

  const onReset = () => {
    const cleared = { code: '', name_th: '', name_en: '', status: '' }
    const clearedSort = { order_by: '', order: '' }
    setSearchParams(cleared)
    setSortInfo(clearedSort)
    getDataBusinessUnit({ page: 1, per_page: pagination.pageSize, params: cleared, sort: clearedSort })
  }

  const content = () => {
    return (
      <Container>
        <Header className="layout-button-add-normal-state">
          <div className="layout-btn-reset-add">
            <Button
              id="reset"
              type="primary"
              className="btn_Secondary2"
              onClick={onReset}
            >
              <RedoOutlined className="Icon-color" />
              {word['Reset']}
            </Button>
          </div>
        </Header>
        <Body>
          <Table
            key="business_unit_table"
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            onChange={onTableChange}
            pagination={pagination}
            showSorterTooltip={false}
          />
        </Body>
      </Container>
    )
  }
  return (
    <BusinessUnitContainer
      style={{ padding: 32 }}
      className="layout-git-master-unitmeasurment"
    >
      {content()}
    </BusinessUnitContainer>
  )
}
export default BusinessUnit
